/**
 * Main Embed Loader
 * The lightweight script that retailers add to their pages
 * Module 2, Section 5.3
 */

import { IframeInjector } from './IframeInjector';
import { DomainValidator } from './DomainValidator';

export interface EmbedLoaderConfig {
  embedId: string;
  token: string;
  viewerBaseUrl?: string;
  container?: string | HTMLElement;
  width?: string;
  height?: string;
}

export class EmbedLoader {
  private static readonly DEFAULT_VIEWER_URL = 'http://localhost:3222';
  
  /**
   * Load embed into page
   */
  public static load(config: EmbedLoaderConfig): HTMLIFrameElement | null {
    try {
      // Validate required parameters
      if (!config.embedId || !config.token) {
        console.error('[EmbedLoader] Missing required parameters: embedId and token');
        return null;
      }
      
      // Get container
      const container = this.resolveContainer(config.container);
      if (!container) {
        console.error('[EmbedLoader] Container not found');
        return null;
      }
      
      // Get viewer URL
      const viewerBaseUrl = config.viewerBaseUrl || this.DEFAULT_VIEWER_URL;
      
      // Log for debugging
      console.info('[EmbedLoader] Loading embed:', {
        embedId: config.embedId,
        viewerBaseUrl,
        domain: DomainValidator.getCurrentDomain()
      });
      
      // Inject iframe
      const iframe = IframeInjector.inject(container, {
        embedId: config.embedId,
        token: config.token,
        viewerBaseUrl,
        width: config.width,
        height: config.height
      });
      
      // Setup error handling
      this.setupErrorHandling(iframe, container);
      
      return iframe;
      
    } catch (error) {
      console.error('[EmbedLoader] Failed to load embed:', error);
      return null;
    }
  }
  
  /**
   * Resolve container element
   */
  private static resolveContainer(container?: string | HTMLElement): HTMLElement | null {
    if (!container) {
      // Use the script's parent element
      const script = document.currentScript as HTMLScriptElement;
      return script?.parentElement || null;
    }
    
    if (typeof container === 'string') {
      return document.querySelector(container);
    }
    
    return container;
  }
  
  /**
   * Setup error handling for iframe
   */
  private static setupErrorHandling(iframe: HTMLIFrameElement, container: HTMLElement): void {
    // Listen for iframe load errors
    iframe.addEventListener('error', () => {
      console.error('[EmbedLoader] Iframe failed to load');
      IframeInjector.createFallback(container, 'Failed to load 3D viewer');
    });
    
    // Timeout for loading
    const timeout = setTimeout(() => {
      // Check if iframe loaded
      if (!iframe.contentWindow) {
        console.warn('[EmbedLoader] Iframe load timeout');
      }
    }, 10000);
    
    iframe.addEventListener('load', () => {
      clearTimeout(timeout);
      console.info('[EmbedLoader] Iframe loaded');
    });
  }
  
  /**
   * Auto-initialize from script attributes
   */
  public static autoInit(): void {
    let script = document.currentScript as HTMLScriptElement;
    
    // Fallback: if currentScript is null, find our script tag by looking for data-embed-id
    if (!script) {
      console.warn('[EmbedLoader] document.currentScript is null, searching for script tag...');
      const scripts = document.querySelectorAll('script[data-embed-id]');
      if (scripts.length > 0) {
        script = scripts[scripts.length - 1] as HTMLScriptElement; // Use last matching script
        console.info('[EmbedLoader] Found script tag via selector');
      } else {
        console.error('[EmbedLoader] No script tag found with data-embed-id');
        return;
      }
    }
    
    const embedId = script.dataset.embedId;
    const token = script.dataset.token;
    const viewerUrl = script.dataset.viewerUrl;
    const container = script.dataset.container;
    const width = script.dataset.width;
    const height = script.dataset.height;
    
    console.info('[EmbedLoader] Script attributes:', {
      embedId,
      token: token ? `${token.substring(0, 20)}...` : 'missing',
      viewerUrl,
      container
    });
    
    if (embedId && token) {
      this.load({
        embedId,
        token,
        viewerBaseUrl: viewerUrl,
        container,
        width,
        height
      });
    } else {
      console.error('[EmbedLoader] Missing required attributes:', { embedId: !!embedId, token: !!token });
    }
  }
}

// Auto-initialize when script loads
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      EmbedLoader.autoInit();
    });
  } else {
    EmbedLoader.autoInit();
  }
}
